import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { ReminderTypeInfo } from '../../types/crm';
import { useCachedPhotoUrl } from '../../hooks/useCachedPhotoUrl';

interface RemindersDropdownProps {
  reminders: ReminderTypeInfo[];
  onReminderClick?: (reminder: ReminderTypeInfo) => void;
  className?: string;
}

const getCategoryColor = (category: string): { bg: string; text: string; border: string } => {
  const colors = {
    medical: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200'
    },
    appointment: {
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200'
    },
    medication: {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200'
    },
    administrative: {
      bg: 'bg-gray-50',
      text: 'text-gray-700',
      border: 'border-gray-200'
    }
  };

  return colors[category as keyof typeof colors] || colors.administrative;
};

const ReminderItem: React.FC<{ reminder: ReminderTypeInfo; onClick: (reminder: ReminderTypeInfo) => void }> = ({ reminder, onClick }) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick(reminder);
      }}
      className="w-full px-3 py-2.5 hover:bg-blue-50 dark:hover:bg-dark-bg-hover transition-colors text-left block"
    >
      <div className="flex items-center gap-3">
        {/* Pet Name Badge */}
        <span className="inline-block px-3 py-1.5 text-sm font-medium text-orange-800 dark:text-orange-300 bg-orange-100 dark:bg-orange-900/30 rounded">
          {reminder.pet_name || 'Unknown'}
        </span>

        {/* Reminder Info */}
        <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary truncate">
          {reminder.name}
        </span>
      </div>
    </button>
  );
};

const RemindersDropdown: React.FC<RemindersDropdownProps> = ({ reminders, onReminderClick, className = '' }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, openUpward: false });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position when it opens
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const dropdownWidth = 320; // w-80 = 320px

      // Estimate dropdown height (each item ~60px + padding)
      const estimatedHeight = Math.min(reminders.length * 60 + 8, 320); // max-h-80 = 320px

      // Determine if should open upward
      const shouldOpenUpward = spaceBelow < estimatedHeight + 20 && rect.top > estimatedHeight + 20;

      // Calculate position
      const top = shouldOpenUpward
        ? rect.top - 8  // Position above button with small margin
        : rect.bottom + 8;

      // Align to right edge of button, but keep within viewport
      const left = Math.max(8, Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 8));

      setDropdownPosition({ top, left, openUpward: shouldOpenUpward });
    }
  }, [isOpen, reminders.length]);

  if (!reminders || reminders.length === 0) {
    return (
      <span className="text-sm text-gray-400 italic">
        None
      </span>
    );
  }

  const handleReminderClick = (reminder: ReminderTypeInfo) => {
    console.log('RemindersDropdown: Reminder clicked', reminder);
    setIsOpen(false);
    
    // Navigate to the reminder edit page using the resourceful URL
    if (reminder.pet_id && reminder.id) {
      navigate(`/dashboard/pets/${reminder.pet_id}/reminders/${reminder.id}/edit`);
    }
    
    if (onReminderClick) {
      onReminderClick(reminder);
    }
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors whitespace-nowrap"
      >
        <span className="whitespace-nowrap">{reminders.length} {reminders.length === 1 ? 'Type' : 'Types'}</span>
        <ChevronDown className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {isOpen && createPortal(
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div
            ref={dropdownRef}
            className="fixed z-50 w-80 bg-white dark:bg-dark-bg-secondary rounded-lg shadow-lg border border-gray-200 dark:border-dark-border py-1 flex flex-col"
            style={{
              [dropdownPosition.openUpward ? 'bottom' : 'top']: dropdownPosition.openUpward
                ? `${window.innerHeight - dropdownPosition.top}px`
                : `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
          >
            {reminders.map((reminder) => (
              <ReminderItem
                key={reminder.id}
                reminder={reminder}
                onClick={handleReminderClick}
              />
            ))}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default RemindersDropdown;

